import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { AnimatePresence, motion } from "motion/react";
import type { SyncOperation } from "@otrack/domain";
import { Icon } from "@otrack/ui";
import { AnswerCard, type Flight } from "./answer-card";
import { runAssistant, type AssistantStep } from "./assistant";
import { routeNewEvents } from "./calendar/destination";
import { enabledCalendars } from "./calendar/google";
import { EXAMPLES, executeCommand, type Answer } from "./commands";
import { useApp, useDataset } from "./context";
import { navigate } from "./router";
import { ghostText, starterCommands, suggest, type Suggestion } from "./suggestions";

/** A suggested command with the part already typed set apart from the part it adds. */
function CommandText({ command, typed }: { command: string; typed: string }) {
  if (!typed || !command.toLowerCase().startsWith(typed.toLowerCase())) return <>{command}</>;
  return <><span className="ot-suggest__typed">{command.slice(0, typed.length)}</span><b>{command.slice(typed.length)}</b></>;
}

export function CommandBar() {
  const { store, act, services, commandRequest, clearCommandRequest, google } = useApp();
  /** Google events the Command Bar and the assistant can list: those of switched-on calendars. */
  const googleView = () => {
    const state = google?.store.getState();
    return { calendars: state && state.phase !== "disconnected" ? enabledCalendars(state.calendars, store.getState().dataset.settings.googleCalendars) : [], events: state?.events ?? {} };
  };
  /** Applies planned changes, sending new Events to the calendar Settings picks for them. */
  const apply = (operations: SyncOperation[], message: string) => {
    const routed = routeNewEvents(operations, store.getState().dataset, google?.store.getState().calendars ?? []);
    return act(routed.operations, routed.calendar ? `${message} (${routed.calendar.name})` : message, { google: routed.google });
  };
  const dataset = useDataset();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLElement>(null);
  const listId = useId();
  const [value, setValue] = useState("");
  const [answer, setAnswer] = useState<Answer | null>(null);
  // The words that led to the open answer, echoed above it.
  const [asked, setAsked] = useState("");
  // Where a typed question starts its trip into the answer's header.
  const [flight, setFlight] = useState<Flight | null>(null);
  // Counts runs, so each new answer replaces the last with a transition.
  const [round, setRound] = useState(0);
  const [steps, setSteps] = useState<AssistantStep[]>([]);
  const [thinking, setThinking] = useState(false);
  const [preview, setPreview] = useState<{ changes: string[]; operations: SyncOperation[] } | null>(null);
  const [focused, setFocused] = useState(false);
  // Hidden with Escape or after running a command, until the User types or asks for it again.
  const [dismissed, setDismissed] = useState(false);
  // The suggestion picked with the arrow keys or the pointer; null follows the preferred one.
  const [cursor, setCursor] = useState<number | null>(null);
  const [overflowing, setOverflowing] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | "auto">("auto");

  const { items, preferred } = useMemo(() => suggest(value, dataset, services.assistant !== null), [value, dataset, services.assistant]);
  const active = cursor !== null && cursor < items.length ? cursor : preferred;
  const listOpen = focused && !dismissed && !thinking && !preview && !answer && items.length > 0;
  const ghost = listOpen && !overflowing ? ghostText(value, items[active]) : "";

  useLayoutEffect(() => {
    const input = inputRef.current;
    setOverflowing(!!input && input.scrollWidth > input.clientWidth + 1);
  }, [value]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target?.closest("input, textarea, select, [contenteditable='true']");
      if (event.key === "/" && !typing) {
        event.preventDefault();
        inputRef.current?.focus();
      }
      if (event.key === "Escape" && (answer || preview)) { setAnswer(null); setPreview(null); }
    };
    const onPointer = (event: PointerEvent) => {
      if ((answer || preview) && !wrapRef.current?.contains(event.target as Node)) { setAnswer(null); setPreview(null); }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer);
    };
  }, [answer, preview]);

  const edit = (text: string) => {
    setValue(text);
    setCursor(null);
    setDismissed(false);
    if (answer && !thinking) setAnswer(null);
  };

  /** Runs the text as a quick command when it is one, otherwise (or when `ask` is set) asks the assistant. */
  const run = async (text: string, ask = false) => {
    if (!text.trim() || thinking) return;
    setAnswer(null);
    setPreview(null);
    setDismissed(true);
    setCursor(null);
    setAsked(text.trim());
    setRound((count) => count + 1);
    setSteps([]);
    const input = inputRef.current;
    if (input && value.trim() === text.trim()) {
      const rect = input.getBoundingClientRect();
      setFlight({ id: Date.now(), x: rect.left, y: rect.top + rect.height / 2, size: parseFloat(getComputedStyle(input).fontSize) || 16 });
    }
    const result = ask ? null : executeCommand(text, store.getState().dataset, store.getState().factory, Date.now(), googleView());
    setValue("");
    if (result?.type === "answer") return setAnswer(result.answer);
    if (result?.type === "navigate") return navigate(result.route);
    if (result?.type === "operations") return apply(result.operations, result.message);
    if (!services.assistant) {
      return setAnswer(services.sync.mode === "signed-out"
        ? { title: "Sign in to use the assistant", text: "Quick commands work offline. Longer requests need ontrack's assistant, which runs once you are signed in.", choices: EXAMPLES }
        : { title: "Not understood", tone: "problem", text: navigator.onLine ? "Only quick commands are available on this build." : "You are offline. Quick commands still work; the assistant is available when you are back online.", choices: EXAMPLES });
    }
    setThinking(true);
    try {
      const outcome = await runAssistant(text, store.getState().dataset, store.getState().factory, Date.now(), services.assistant, (step) => {
        setSteps((current) => current.at(-1) === step ? current : [...current, step]);
      }, googleView());
      const confirm = outcome.operations.length > 0 && outcome.needsConfirmation;
      if (confirm) setPreview({ changes: outcome.changes, operations: outcome.operations });
      else if (outcome.operations.length) await apply(outcome.operations, outcome.changes.join(". "));
      // Models sometimes add Markdown emphasis despite the instructions; show plain text.
      const plain = outcome.answer.replace(/\*\*(.+?)\*\*/g, "$1").replace(/^#+\s*/gm, "").trim();
      const done = confirm ? [] : outcome.changes;
      if (plain || done.length || outcome.sources.length) setAnswer({ title: "ontrack", text: plain, reply: true, done, sources: outcome.sources });
    } catch (error) {
      setAnswer({ title: "The assistant is unavailable", tone: "problem", text: error instanceof Error ? error.message : "Try again in a moment." });
    } finally {
      setThinking(false);
    }
  };

  useEffect(() => {
    if (!commandRequest) return;
    clearCommandRequest();
    if (commandRequest.run) void run(commandRequest.text);
    else {
      edit(commandRequest.text);
      inputRef.current?.focus();
    }
    // Only a new request should trigger this, not the callbacks it uses.
  }, [commandRequest?.id]);

  const confirmPreview = async () => {
    if (!preview) return;
    await apply(preview.operations, preview.changes.join(". "));
    setPreview(null);
    setAnswer(null);
    // A newly drafted or split invoice opens, so it can be reviewed before it is sent.
    const drafted = preview.operations.find((operation) => operation.entityType === "invoice" && ("clientId" in operation.patch || "installment" in operation.patch));
    if (drafted) navigate({ page: "invoice", id: drafted.entityId });
  };

  const pick = (command: string) => {
    if (command.endsWith(" ")) {
      edit(command);
      inputRef.current?.focus();
    } else void run(command);
  };

  const choose = (item: Suggestion) => {
    if (item.kind === "typed") void run(value);
    else if (item.kind === "ask") void run(value, true);
    else if (item.partial) edit(item.command);
    else void run(item.command);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!listOpen) return setDismissed(false);
      const step = event.key === "ArrowDown" ? 1 : -1;
      setCursor(active < 0 ? (step > 0 ? 0 : items.length - 1) : (active + step + items.length) % items.length);
    } else if (event.key === "Enter" && listOpen && active >= 0) {
      event.preventDefault();
      choose(items[active]!);
    } else if (event.key === "Tab" && !event.shiftKey && listOpen && active >= 0 && !["typed", "ask"].includes(items[active]!.kind)) {
      event.preventDefault();
      edit(items[active]!.command);
    } else if (event.key === "ArrowRight" && ghost && event.currentTarget.selectionStart === value.length) {
      event.preventDefault();
      edit(items[active]!.command);
    } else if (event.key === "Escape" && listOpen) {
      event.preventDefault();
      setDismissed(true);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void run(value);
  };

  const open = thinking || preview !== null || answer !== null;
  const phase = thinking ? "thinking" : `${preview ? "preview" : "answer"}-${round}`;

  // The panel follows its content's height, so swapping thinking for the answer grows smoothly.
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!open || !scroller || typeof ResizeObserver === "undefined") return;
    setHeight(scroller.offsetHeight);
    const observer = new ResizeObserver(() => setHeight(scroller.offsetHeight));
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [open]);

  return <section className={`ot-command ${open || listOpen ? "is-open" : ""}`} ref={wrapRef}>
    <form className="ot-command__bar" onSubmit={onSubmit}>
      <div className="ot-command__field">
        {ghost && <span className="ot-command__ghost" aria-hidden="true"><span>{value}</span>{ghost}<kbd className="ot-command__tab">Tab</kbd></span>}
        <input
          ref={inputRef}
          value={value}
          onChange={(event) => edit(event.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); setDismissed(false); setCursor(null); }}
          role="combobox"
          aria-expanded={listOpen}
          aria-controls={listId}
          aria-autocomplete="both"
          aria-activedescendant={listOpen && active >= 0 ? `${listId}-${active}` : undefined}
          autoComplete="off"
          spellCheck={false}
          aria-label="Command bar"
          placeholder="Tell ontrack what you did, or ask about your money"
        />
      </div>
      {!value && <kbd aria-hidden="true">/</kbd>}
      <button className={`ot-command__run ${value.trim() ? "is-ready" : ""}`} type="submit" disabled={thinking} aria-label="Run">
        {thinking ? <span className="ot-command__dots" aria-hidden="true"><i/><i/><i/></span> : <Icon name="return" weight="bold"/>}
      </button>
    </form>
    <div className="ot-command__chips">
      {starterCommands(dataset).map((suggestion) => <button key={suggestion} type="button" className="ot-chip" onClick={() => void run(suggestion)}>{suggestion}</button>)}
    </div>
    <AnimatePresence>
      {listOpen && <motion.div
        key="suggest"
        className="ot-suggest"
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4, transition: { duration: 0.12 } }}
      >
        <div id={listId} role="listbox" aria-label="Suggestions" className="ot-suggest__list">
          {items.map((item, index) => <div
            key={`${item.kind}:${item.command}`}
            id={`${listId}-${index}`}
            role="option"
            aria-selected={index === active}
            className={`ot-suggest__item ot-suggest__item--${item.kind} ${index === active ? "is-active" : ""}`}
            onMouseDown={(event) => event.preventDefault()}
            onMouseMove={() => { if (index !== active) setCursor(index); }}
            onClick={() => choose(item)}
          >
            <span className="ot-suggest__icon"><Icon name={item.icon}/></span>
            {item.kind === "typed" || item.kind === "ask"
              ? <span className="ot-suggest__main">{item.hint}</span>
              : <>
                <span className="ot-suggest__main ot-suggest__command"><CommandText command={item.command} typed={value}/></span>
                <span className="ot-suggest__hint">{item.hint}</span>
              </>}
            <kbd className="ot-suggest__key" aria-hidden="true">{item.partial ? "Tab" : "Enter"}</kbd>
          </div>)}
        </div>
        <div className="ot-suggest__foot" aria-hidden="true">
          <span><kbd>↑</kbd><kbd>↓</kbd>choose</span>
          <span><kbd>Tab</kbd>complete</span>
          <span><kbd>Enter</kbd>run</span>
          <span><kbd>Esc</kbd>close</span>
        </div>
      </motion.div>}
      {open && <motion.div
        key="answer"
        className="ot-answer"
        aria-live="polite"
        initial={{ opacity: 0, y: -8, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1, height }}
        exit={{ opacity: 0, y: -6, scale: 0.99, transition: { duration: 0.16 } }}
        transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1], height: { duration: 0.48, ease: [0.16, 1, 0.3, 1] } }}
      >
        <div className="ot-answer__scroll" ref={scrollRef}><AnswerCard
          asked={asked}
          flight={flight}
          phase={phase}
          thinking={thinking}
          steps={steps}
          preview={preview}
          answer={answer}
          onClose={() => { setAnswer(null); setPreview(null); }}
          onPick={pick}
          onCancel={() => { setPreview(null); setAnswer(null); }}
          onApply={() => void confirmPreview()}
        /></div>
      </motion.div>}
    </AnimatePresence>
  </section>;
}
